<script setup>
/* Auswahl der Schlagwörter (Player Info > Edit details): bis zu drei aus der festen Liste (domain/playerKeywords.js).
 *
 * Gewählte stehen als Chips mit Entfernen-Knopf (wie in der Ansicht). Darunter ein Knopf "Choose keywords", der ein
 * Feld mit den GRUPPIERTEN Chips aufklappt (Physical / Technical / Mental / Goalkeeper; die Chips brechen in Zeilen
 * um, kein innerer Scrollkasten). Antippen wählt oder entfernt; sind drei gewählt, sind die übrigen abgeblendet.
 * Altwerte außerhalb der Liste erscheinen als normaler Chip, zählen mit und lassen sich entfernen.
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import KeywordChip from './KeywordChip.vue';
import { KEYWORD_GROUPS, keywordKeyOf, keywordLabel, normalizeTraits, toggleTrait } from '../domain/playerKeywords.js';
import { TRAITS_MAX } from '../domain/playerCard.js';

const model = defineModel({ type: Array, default: () => [] });
const { t } = useI18n();

const open = ref(false);
const selected = computed(() => normalizeTraits(model.value));
const full = computed(() => selected.value.length >= TRAITS_MAX);
const isOn = (key) => selected.value.some((v) => keywordKeyOf(v) === key);
const name = (value) => keywordLabel(value, t);

function toggle(value) {
  model.value = toggleTrait(model.value, value);
}
</script>

<template>
  <div class="kw" data-testid="keyword-select">
    <div class="kw-chips" aria-live="polite">
      <KeywordChip
        v-for="v in selected"
        :key="v"
        :label="name(v)"
        removable
        :remove-label="$t('player.keywordRemove', { name: name(v) })"
        data-testid="keyword-chip"
        @remove="toggle(v)"
      />
      <span v-if="!selected.length" class="kw-none">{{ $t('player.keywordsNone') }}</span>
    </div>
    <p class="kw-count" :class="{ full }" data-testid="keyword-count">
      {{ $t('player.keywordsCount', { n: selected.length, max: TRAITS_MAX }) }}<template v-if="full"> · {{ $t('player.keywordsMax') }}</template>
    </p>

    <button
      type="button"
      class="btn ghost small kw-toggle"
      :aria-expanded="open"
      aria-controls="kw-panel"
      data-testid="keyword-toggle"
      @click="open = !open"
    >
      {{ $t('player.keywordsChoose') }}
      <v-icon :icon="open ? mdiChevronUp : mdiChevronDown" size="16" />
    </button>

    <div v-if="open" id="kw-panel" class="kw-panel" role="group" :aria-label="$t('player.keywords')" data-testid="keyword-panel">
      <section v-for="g in KEYWORD_GROUPS" :key="g.key" class="kw-group">
        <h4>{{ $t(`keywordGroups.${g.key}`) }}</h4>
        <div class="kw-wrap">
          <KeywordChip
            v-for="k in g.keywords"
            :key="k"
            :label="$t(`keywords.${k}`)"
            selectable
            :selected="isOn(k)"
            :disabled="full && !isOn(k)"
            data-testid="keyword-option"
            :data-key="k"
            @toggle="toggle(k)"
          />
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
.kw {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
}
.kw-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 30px;
  align-items: center;
}
.kw-none {
  font-size: var(--pv-meta, 13px);
  color: var(--dim);
}
.kw-count {
  margin: 0;
  font-size: var(--pv-meta, 13px);
  color: var(--muted);
}
.kw-count.full {
  color: var(--text);
}
.kw-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
}
.kw-panel {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--card);
}
.kw-group h4 {
  margin: 0 0 8px;
  font-size: var(--pv-label, 11px);
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--muted);
}
/* Chips brechen in Zeilen um; die Seite scrollt, nicht das Feld */
.kw-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
